import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useAlertSavedQueryStatusWithoutSpecialty } from "@app/contexts/useAlerts/useAlertStatus";
import { BarChartContainer } from "./barChart";
import { PieChartContainer } from "./pieChart";
import { useNavigate } from 'react-router-dom';

const metricsChartMap = {
    bar: BarChartContainer,
    pie: PieChartContainer,
};

const MetricDataProcessor = ({ alert, onProcessComplete }) => {
    const { savedQueryCount } = useAlertSavedQueryStatusWithoutSpecialty({ alert });

    useEffect(() => {
        onProcessComplete({
            ...alert,
            title: alert.displayName,
            label: alert.description,
            count: savedQueryCount,
            order: alert.order || "9999",
            chartType: alert.type || "bar",
        });
    }, [savedQueryCount, alert, onProcessComplete]);

    return null;
};


export const DashboardMetricsContainer = ({ metricsAlerts = [] }) => {
    const [processedData, setProcessedData] = useState([]);
    const sortedMetricsAlerts = useMemo(() => {
        return metricsAlerts?.sort((a, b) => {
            // Sort by displayName first, then by type
            const displayNameComparison = a.displayName.localeCompare(b.displayName);
            if (displayNameComparison !== 0) return displayNameComparison;
            return a?.type?.localeCompare(b.type);
        });
    }, [metricsAlerts]);
    const metricsAlertsRef = useRef(sortedMetricsAlerts);
    const navigate = useNavigate();

    const handleProcessComplete = useCallback((data) => {
        setProcessedData((prev) => {
            // Prevent duplicates
            const exists = prev.some((item) => item.alertId === data.alertId);
            return exists ? prev : [...prev, data];
        });
    }, []);

    useEffect(() => {
        // Only reset if metricsAlerts actually changed
        if (metricsAlertsRef.current !== metricsAlerts) {
            setProcessedData([]);
            metricsAlertsRef.current = metricsAlerts;
        }
    }, [metricsAlerts]);

    // Memoize the processed data to avoid unnecessary recalculations
    const mapProcssedData = useMemo(() => {
        return processedData.reduce((acc, item) => {
            const { displayName, chartType, order } = item;
            const key = `${displayName}-${chartType}`;
            if (!acc[key]) {
                acc[key] = {
                    displayName,
                    chartType,
                    order,
                    data: [],
                };
            }
            acc[key].data.push(item);
            return acc;
        }, {});
    }, [processedData]);

    const generateDisplayName = (displayName) => {
        const getName = displayName.split('.')[1] ?? "Metrics";
        return getName.startsWith(' ') ? getName.slice(1) : getName;
    };

    return (
        <>
            {!metricsAlerts &&
                <div className="text-center text-gray-500 text-3xl">
                    No metrics available.
                </div>
            }
            {metricsAlerts.map((alert) => (
                <MetricDataProcessor
                    key={alert.alertId}
                    alert={alert}
                    onProcessComplete={handleProcessComplete}
                />
            ))}
            {processedData.length === 0 ? (
                <div className="text-center text-gray-500 text-3xl">
                    No metrics available.
                </div>
            ) : (
                <>
                    {Object.keys(mapProcssedData).map((metric) => {
                        const dataItem = mapProcssedData[metric];
                        const { displayName, chartType } = dataItem;
                        const ChartComponent = metricsChartMap[chartType];

                        if (!ChartComponent) {
                            console.warn(`No chart component found for type: ${chartType}`);
                            return null;
                        }

                        return (
                            <div className="h-[25rem] pb-4" key={`${chartType}-${displayName}`}>
                                <ChartComponent
                                    title={generateDisplayName(displayName)}
                                    key={`${displayName}-${chartType}`}
                                    data={mapProcssedData[`${displayName}-${chartType}`]?.data.sort((graphA, graphB) => parseInt(graphA.order) - parseInt(graphB.order)) || []}
                                    chartOptions={{
                                        series: {
                                            listeners: {
                                                nodeClick: (event) => navigate(event.datum.path),
                                            },
                                        },
                                    }}
                                />
                            </div>
                        );
                    })}
                </>
            )}
        </>
    );
};